/// <amd-module name="Pipeline/Analytics/Common/Sources/PipelineTrendSource" />
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { IODataQueryResponse, IReportContext, TrendBy } from "Analytics/Shared/Common/Types";
import { parseReturnDateIntoDisplayForm } from "Analytics/Shared/Common/Utils/DateAdjuster";
import { ITrendChartData, ITrendChartReportConfiguration } from "Analytics/Shared/TrendChart/Common/Types";
import * as CommonResources from "Pipeline/Analytics/Common/Resources";
import { PipelineReportSource } from "Pipeline/Analytics/Common/Sources/PipelineReportSource";
import { IPipelineOutcomeData, IPipelineOutcomePassRateData, ITopFailingStageAndTaskData } from "Pipeline/Analytics/Common/Types";

interface IODataPassRateTrend {
    CompletedDateSK: number;
    FailedCount: number;
    PartiallySucceededCount: number;
    SucceededCount: number;
    TotalCount: number;
}

export abstract class PipelineTrendSource extends PipelineReportSource {
    public async _queryPassRateTrendData(queryOptions: ODataQueryOptions, confValues: ITrendChartReportConfiguration): Promise<IPipelineOutcomeData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("Data is empty");
        }

        let pipelineOutcomeData: IPipelineOutcomeData;
        const chartData: ITrendChartData = {
            primaryChartData: { [this._emptyString]: [] },
            secondaryChartData: [],
            confValues: confValues,
            isCachedData: data.isCachedData
        };

        let pipelineOutcomePassRateData: IPipelineOutcomePassRateData;
        let totalSucceededCount = 0;
        let totalPartiallySucceededCount = 0;
        let totalFailedCount = 0;
        let totalCount = 0;

        // Iterate over all data
        (data.value as IODataPassRateTrend[]).forEach(d => {
            const date = parseReturnDateIntoDisplayForm(TrendBy.Days, d);
            const failedCount = this._parseFailedCount(d);

            const totalCountForDay = this._parseTotalCount(d);
            chartData.primaryChartData[this._emptyString].push({
                date: date,
                aggrValue: failedCount,
                customData: {
                    label: CommonResources.TotalPipelines,
                    value: totalCountForDay
                }
            });

            const succeededCount = this._parseSucceededCount(d);
            const partiallySucceededCount = this._parsePartiallySucceededCount(d);
            const passPercentage = this._getPassPercentage(totalCountForDay, succeededCount, partiallySucceededCount);
            totalCount += totalCountForDay;
            totalSucceededCount += succeededCount;
            totalPartiallySucceededCount += partiallySucceededCount;
            totalFailedCount += this._parseFailedCount(d);
            if (!chartData.secondaryChartData) {
                chartData.secondaryChartData = [];
            }
            chartData.secondaryChartData.push({
                date: date,
                aggrValue: passPercentage
            });
        });
        pipelineOutcomePassRateData = {
            totalCount: totalCount,
            failedCount: totalFailedCount,
            passedCount: totalSucceededCount + totalPartiallySucceededCount,
            passPercentage: this._getPassPercentage(totalCount, totalSucceededCount, totalPartiallySucceededCount)
        };
        pipelineOutcomeData = {
            trendChartData: chartData,
            pipelineOutcomePassRateData: pipelineOutcomePassRateData
        };

        return pipelineOutcomeData;
    }

    public async getTopFailingStageAndTaskData(
        reportContext: IReportContext,
        confValues: ITrendChartReportConfiguration
    ): Promise<ITopFailingStageAndTaskData> {
        const projectId = this._getProjectId();
        const queryOptions = {
            entityType: this._buildTaskResultsEntityName,
            project: projectId
        } as ODataQueryOptions;

        const result = await this.getStages(reportContext, confValues, false);
        const topFailingStageAndTaskData: ITopFailingStageAndTaskData = { stageName: "", stageFailureCount: 0, taskName: "", failedCount: 0 };
        let filterStr = this._getTopFailingTaskQueryFilter(reportContext, confValues);
        const groupByBuildAndTaskStr = `groupby((${this._getGroupByBuildId()},${this._getGroupByTask()}), aggregate(${this._getCountForAggregationAsFailedCount()}))`;
        const groupByTaskStr = `groupby((${this._getGroupByTask()}), aggregate(${this._getCountForAggregationAsFailedCount()}))`;
        queryOptions.$orderby = ` FailedCount desc `;
        if (result && result.length > 1) {
            const stageWithMaxFailures = result.sort((a, b) => b.dataCount - a.dataCount)[0];
            filterStr = this._getTopFailingTaskQueryFilterForStage(reportContext, confValues, stageWithMaxFailures.fullName);
            topFailingStageAndTaskData.stageName = stageWithMaxFailures.name;
            topFailingStageAndTaskData.stageFailureCount = stageWithMaxFailures.dataCount;
        }
        queryOptions.$apply = `${filterStr}/${groupByBuildAndTaskStr}/${groupByTaskStr}`;

        return this._getTopFailingTask(queryOptions, topFailingStageAndTaskData);
    }

    private async _getTopFailingTask(
        queryOptions: ODataQueryOptions,
        topFailingStageAndTaskData: ITopFailingStageAndTaskData
    ): Promise<ITopFailingStageAndTaskData> {
        const data = await this.queryOData(queryOptions);
        if (!data || !data.value) {
            throw new Error("data is empty.");
        }

        const dataValues = data.value as any[];
        if (dataValues && dataValues.length > 0) {
            const topDataValue = dataValues[0];
            topFailingStageAndTaskData.taskName = this._parseTaskDisplayName(topDataValue);
            topFailingStageAndTaskData.failedCount = this._parseFailedCount(topDataValue);
            if (!topFailingStageAndTaskData.stageFailureCount) {
                const totalTaskFailures = dataValues.reduce((prev, curr) => {
                    return prev + curr.FailedCount;
                }, 0);
                topFailingStageAndTaskData.stageFailureCount = totalTaskFailures;
            }
        }

        return topFailingStageAndTaskData;
    }
}
